import type { HTTPService } from "@soft-db/http";

export type Latency = "latency";
export type Network = "measuring" | "offline" | "idle";
export type Battery = "battery_soc";

export type MetricType = Latency | Network | Battery;

export type MetricData = {
	type: MetricType;
	value: number;
	movingAverage: number;
};

export type Metrics = {
	time: Date;
	metrics: MetricData[];
};

export enum HealthStatus {
	OK = "ok",
	WARNING = "warning",
	ERROR = "error",
}

export type BaseReport = {
	status: HealthStatus;
	lastUpdate: Date;
};

export type DataLatencyHealthReport = {
	avg: number;
} & BaseReport;

export type NetworkStatusHealthReport = {
	measuring: number;
	idle: number;
	offline: number;
} & BaseReport;

export type AveragableHealthReport = {
	avg: number;
	decreasing: boolean;
} & BaseReport;

export type HealthReportStationSummary = {
	id: string;
	name: string;
};

export type DataUsageReport = {
	records: number;
	events: number;
} & BaseReport;

export type HealthReport = {
	status: HealthStatus;
	range: {
		start: Date;
		end: Date;
	};
	dataLatency?: DataLatencyHealthReport;
	state?: NetworkStatusHealthReport;
	battery?: {
		soc?: AveragableHealthReport;
		voltage?: AveragableHealthReport;
	};
	inputVoltage?: AveragableHealthReport;
	dataUsage?: DataUsageReport;
	station: HealthReportStationSummary;
};

export type StationHealthReportRange = {
	start: Date;
	end: Date;
};

export class StationHealthApi {
	constructor(private _http: HTTPService) {}

	async generateStationReport(id: string, range: StationHealthReportRange) {
		return this._http.get<HealthReport>(`/stations/${id}/health`, {
			params: { time: range.start, endTime: range.end },
		});
	}

	async fetchStationMetrics(id: string, range: StationHealthReportRange, period: number) {
		return this._http.get<Metrics[]>(`/stations/${id}/metrics`, {
			params: {
				time: range.start,
				endTime: range.end,
				period,
				movingAverageMemory: 1_440,
			},
		});
	}

	async generateSiteReport(id: string, range: StationHealthReportRange) {
		return this._http.get<HealthReport>(`/sites/${id}/health`, {
			params: { time: range.start, endTime: range.end },
		});
	}

	async fetchSiteMetrics(id: string, range: StationHealthReportRange, period: number) {
		return this._http.get<Metrics[]>(`/sites/${id}/metrics`, {
			params: {
				time: range.start,
				endTime: range.end,
				period,
				movingAverageMemory: 1_440,
			},
		});
	}
}
